import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { admins } from "../../Constants";
import { getAllUploadedDemos, AdminUploadedDemo } from "../../services/UserDemoService";
import Loading from "../../components/Loading";

const card: React.CSSProperties = {
    background: "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(37,37,37,0.95) 100%)",
    border: "1px solid rgba(255,102,0,0.15)",
    borderRadius: 12,
    padding: "16px 20px",
    minWidth: 120,
    textAlign: "center",
};

const statusStyle = (s: string): React.CSSProperties => {
    const map: Record<string, [string, string]> = {
        completed: ["rgba(74,222,128,0.2)", "#4ade80"],
        processing: ["rgba(255,102,0,0.15)", "#ff9933"],
        failed: ["rgba(248,113,113,0.2)", "#f87171"],
    };
    const [bg, color] = map[s] ?? map["processing"];
    return { background: bg, color, borderRadius: 12, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase" };
};

const AdminUploadedDemosPage: React.FC = () => {
    const { user, authLoaded } = useContext(AuthContext);
    const isAdmin = !!user && admins.includes(user.steamID);

    const [demos, setDemos] = useState<AdminUploadedDemo[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isAdmin) return;
        getAllUploadedDemos().then(setDemos).catch(() => setError("Could not load uploaded demos."));
    }, [isAdmin]);

    // Group by uploader, preserving newest-first order.
    const groups = useMemo(() => {
        const m = new Map<string, { name: string | null; steamId: string; demos: AdminUploadedDemo[] }>();
        for (const d of demos ?? []) {
            if (!m.has(d.ownerSteamId)) m.set(d.ownerSteamId, { name: d.ownerName, steamId: d.ownerSteamId, demos: [] });
            m.get(d.ownerSteamId)!.demos.push(d);
        }
        return Array.from(m.values());
    }, [demos]);

    if (authLoaded && !isAdmin) return <div className="container mt-4"><div className="alert alert-danger">Admins only.</div></div>;
    if (error) return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
    if (!demos) return <Loading />;

    const matchup = (d: AdminUploadedDemo) =>
        d.team1Name && d.team2Name ? `${d.team1Name} vs ${d.team2Name}` : (d.originalFilename ?? "—");

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0">Uploaded Demos</h3>
                <Link to="/admin" style={{ color: "#ff9933", textDecoration: "none" }}>← Admin</Link>
            </div>

            <div className="d-flex gap-3 mb-4 flex-wrap">
                <div style={card}><div style={{ fontSize: "1.6rem", fontWeight: 700 }}>{groups.length}</div><div style={{ color: "rgba(255,255,255,0.6)" }}>Uploaders</div></div>
                <div style={card}><div style={{ fontSize: "1.6rem", fontWeight: 700 }}>{demos.length}</div><div style={{ color: "rgba(255,255,255,0.6)" }}>Total demos</div></div>
                <div style={card}><div style={{ fontSize: "1.6rem", fontWeight: 700 }}>{demos.filter(d => d.status === "failed").length}</div><div style={{ color: "rgba(255,255,255,0.6)" }}>Failed</div></div>
            </div>

            {groups.length === 0 && <div className="alert alert-info">No demos have been uploaded yet.</div>}

            {groups.map(g => (
                <div key={g.steamId} className="mb-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <Link to={`/player/${g.steamId}`} style={{ fontWeight: 700, fontSize: "1.05rem", color: "var(--text-primary,#e2e8f0)", textDecoration: "none" }}>
                            {g.name || g.steamId}
                        </Link>
                        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>{g.steamId} · {g.demos.length} demo{g.demos.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div style={{ overflowX: "auto", background: "linear-gradient(135deg,#1a1a1a,#252525)", borderRadius: 12, border: "1px solid rgba(255,102,0,0.1)" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", color: "var(--text-primary,#e2e8f0)" }}>
                            <thead style={{ background: "linear-gradient(135deg,#2a2a2a,#333)", borderBottom: "2px solid rgba(255,102,0,0.15)" }}>
                                <tr>
                                    {["Match", "Map", "Status", "Uploaded", ""].map((h, i) => (
                                        <th key={i} style={{ padding: "10px 16px", textAlign: i === 4 ? "right" : "left", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", color: "rgba(255,255,255,0.9)" }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {g.demos.map(d => (
                                    <tr key={d.uploadId} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                                        <td style={{ padding: "10px 16px" }}>{matchup(d)}</td>
                                        <td style={{ padding: "10px 16px" }}>{d.mapName ?? "—"}</td>
                                        <td style={{ padding: "10px 16px" }}>
                                            <span style={statusStyle(d.status)}>{d.status}</span>
                                            {d.status === "failed" && d.errorMessage && <div style={{ color: "#f87171", fontSize: "0.75rem", marginTop: 4 }}>{d.errorMessage}</div>}
                                        </td>
                                        <td style={{ padding: "10px 16px", color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>{new Date(d.createdAt).toLocaleString()}</td>
                                        <td style={{ padding: "10px 16px", textAlign: "right" }}>
                                            {d.status === "completed" && <Link to={`/demo/${d.uploadId}`} style={{ color: "#ff9933", textDecoration: "none", fontWeight: 600 }}>View</Link>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default AdminUploadedDemosPage;
